import React, { useState, useEffect } from 'react';
import { BusinessReportHTML } from './BusinessReportHTML';
import { BusinessReportRTF } from './BusinessReportRTF';
import { BusinessReportPlain } from './BusinessReportPlain';
import { BusinessReportPDF } from './BusinessReportPDF';
import { BusinessReportExcel } from './BusinessReportExcel';
import { FileText, FileCode, File, File as FilePdf, FileSpreadsheet, X } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';

interface BusinessReportManagerProps {
  content: string;
  onClose?: () => void;
  conversationTitle?: string;
}

/**
 * Component that manages different types of business reports
 * and allows the user to select which format to generate
 */
export const BusinessReportManager: React.FC<BusinessReportManagerProps> = ({ 
  content,
  onClose,
  conversationTitle
}) => {
  const { user } = useAuthStore();
  const [title, setTitle] = useState(conversationTitle || 'Informe Empresarial');
  const [companyName, setCompanyName] = useState('');
  const [selectedTab, setSelectedTab] = useState<'html' | 'rtf' | 'plain' | 'pdf' | 'excel'>('pdf');
  const [isLoading, setIsLoading] = useState(false);

  // Fetch user's company name when component mounts
  useEffect(() => {
    const fetchUserCompany = async () => {
      if (!user) return;
      
      setIsLoading(true);
      try {
        // First get the user's company ID
        const { data: userData, error: userError } = await supabase
          .from('usuarios')
          .select('empresa_id')
          .eq('id', user.id)
          .single();
          
        if (userError) throw userError;
        
        if (userData?.empresa_id) {
          // Then get the company name
          const { data: companyData, error: companyError } = await supabase
            .from('empresas')
            .select('nombre')
            .eq('id', userData.empresa_id)
            .single();
            
          if (companyError) throw companyError;
          
          if (companyData?.nombre) {
            setCompanyName(companyData.nombre);
          } else {
            setCompanyName('Su Empresa');
          }
        } else {
          setCompanyName('Su Empresa');
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        setCompanyName('Su Empresa');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserCompany();
  }, [user]);

  // Detect if content is from a legal document
  const isLegalDocument = React.useMemo(() => {
    const legalTerms = [
      'artículo', 'ley', 'real decreto', 'normativa', 'legislación',
      'jurídico', 'legal', 'normas', 'reglamento', 'disposición'
    ];
    
    const contentLower = content.toLowerCase();
    return legalTerms.some(term => contentLower.includes(term));
  }, [content]);

  // Set appropriate title based on content
  React.useEffect(() => {
    if (conversationTitle) {
      setTitle(conversationTitle);
    } else if (isLegalDocument) {
      setTitle('Informe Legal');
    } else if (content.includes('Usuario:') && content.includes('Asistente:')) {
      setTitle('Transcripción de Conversación');
    }
  }, [isLegalDocument, content, conversationTitle]);

  // Clean up content for better formatting
  const cleanedContent = React.useMemo(() => {
    let processed = content;
    
    // Fix common formatting issues
    processed = processed.replace(/\s+\d+:\d+†[^\n]+\d+\s*/g, ' '); // Remove page markers like "08:4†BOE-A-1992-28740-consolidado.pdf0"
    processed = processed.replace(/\s{2,}/g, ' '); // Replace multiple spaces with a single space
    processed = processed.replace(/\n{3,}/g, '\n\n'); // Replace multiple newlines with double newlines
    
    // Improve list formatting
    processed = processed.replace(/•\s+/g, '• '); // Ensure consistent bullet point spacing
    
    // Improve heading formatting
    processed = processed.replace(/(\d+\.\s*\*\*[^*]+\*\*)/g, '\n$1\n'); // Add newlines around numbered headings
    
    return processed;
  }, [content]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Generador de Informes Empresariales</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Título del Informe
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Título del informe"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de la Empresa
            </label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nombre de la empresa"
              disabled={isLoading}
            />
            {isLoading && (
              <p className="text-xs text-gray-500 mt-1">Cargando información de la empresa...</p>
            )}
          </div>
        </div>

        <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
          <button
            className={`py-2 px-4 font-medium text-sm flex items-center ${
              selectedTab === 'pdf' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setSelectedTab('pdf')}
          >
            <FilePdf className="w-4 h-4 mr-2" />
            PDF
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm flex items-center ${
              selectedTab === 'html' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setSelectedTab('html')}
          >
            <FileCode className="w-4 h-4 mr-2" />
            HTML
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm flex items-center ${
              selectedTab === 'excel' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setSelectedTab('excel')}
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm flex items-center ${
              selectedTab === 'rtf' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setSelectedTab('rtf')}
          >
            <FileText className="w-4 h-4 mr-2" />
            RTF
          </button>
          <button
            className={`py-2 px-4 font-medium text-sm flex items-center ${
              selectedTab === 'plain' 
                ? 'text-blue-600 border-b-2 border-blue-500' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
            onClick={() => setSelectedTab('plain')}
          >
            <File className="w-4 h-4 mr-2" />
            Texto Plano
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          {selectedTab === 'html' && (
            <BusinessReportHTML 
              title={title} 
              content={cleanedContent} 
              companyName={companyName} 
            />
          )}
          
          {selectedTab === 'pdf' && (
            <BusinessReportPDF 
              title={title} 
              content={cleanedContent} 
              companyName={companyName} 
            />
          )}
          
          {selectedTab === 'excel' && (
            <BusinessReportExcel 
              title={title} 
              content={cleanedContent} 
              companyName={companyName} 
            />
          )}
          
          {selectedTab === 'rtf' && (
            <BusinessReportRTF 
              title={title} 
              content={cleanedContent} 
              companyName={companyName} 
            />
          )}
          
          {selectedTab === 'plain' && (
            <BusinessReportPlain 
              title={title} 
              content={cleanedContent} 
              companyName={companyName} 
            />
          )}
        </div>

        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-4">
            Seleccione el formato deseado y personalice los detalles del informe antes de descargarlo.
            Cada formato tiene diferentes características y compatibilidad con aplicaciones.
          </p>
          
          <div className="flex justify-end">
            {onClose && (
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md mr-2"
              >
                Cancelar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};